/**
 * JSON 查看器（旧 components/common/JsonViewer）：格式化 / 原始 / 树形三种视图，
 * 支持复制、下载、树形全部展开/折叠，底部显示大小、行数、节点数。
 */
import { useMemo, useState, type ReactNode } from 'react'
import { App, Button, Divider, Empty, Result, Space, Tag } from 'antd'
import {
  ApartmentOutlined,
  CodeOutlined,
  CopyOutlined,
  DownloadOutlined,
  ExpandAltOutlined,
  FileTextOutlined,
  ShrinkOutlined
} from '@ant-design/icons'
import { JsonTreeNode } from './JsonTreeNode'
import styles from './JsonViewer.module.css'

type ViewMode = 'formatted' | 'raw' | 'tree'

interface JsonViewerProps {
  // 对象、数组，或 JSON 字符串
  data: unknown
  title?: string
}

type ParseResult = { ok: true; value: unknown } | { ok: false; error: string }

function parse(data: unknown): ParseResult {
  try {
    return { ok: true, value: typeof data === 'string' ? JSON.parse(data) : data }
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) }
  }
}

function countNodes(value: unknown): number {
  if (value === null || value === undefined) return 0
  if (typeof value !== 'object') return 1
  return 1 + Object.values(value).reduce<number>((sum, child) => sum + countNodes(child), 0)
}

function formatSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }
  return `${Math.round(size * 100) / 100} ${units[unitIndex]}`
}

export function JsonViewer({ data, title }: Readonly<JsonViewerProps>) {
  const { message } = App.useApp()
  const [viewMode, setViewMode] = useState<ViewMode>('formatted')
  const [allExpanded, setAllExpanded] = useState(true)

  const parsed = useMemo(() => parse(data), [data])
  const isEmpty = parsed.ok && (parsed.value === null || parsed.value === undefined)
  const content = useMemo(() => {
    if (!parsed.ok || isEmpty) return ''
    return viewMode === 'raw' ? JSON.stringify(parsed.value) : JSON.stringify(parsed.value, null, 2)
  }, [parsed, isEmpty, viewMode])

  let status = { color: 'success', text: '正常' }
  if (!parsed.ok) {
    status = { color: 'error', text: '解析失败' }
  } else if (isEmpty) {
    status = { color: 'warning', text: '无数据' }
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      message.success('已复制到剪贴板')
    } catch {
      message.error('复制失败')
    }
  }

  const download = () => {
    const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `data-${Date.now()}.json`
    link.click()
    URL.revokeObjectURL(url)
    message.success('下载完成')
  }

  let bodyContent: ReactNode
  if (!parsed.ok) {
    bodyContent = <Result status="error" title="JSON解析错误" subTitle={parsed.error} />
  } else if (isEmpty) {
    bodyContent = <Empty className={styles.empty} description="暂无数据" />
  } else if (viewMode === 'tree') {
    bodyContent = (
      <div className={styles.tree}>
        {/* 切换全部展开/折叠时重新挂载，让每个节点按新状态初始化 */}
        <JsonTreeNode key={String(allExpanded)} data={parsed.value} root expanded={allExpanded} />
      </div>
    )
  } else {
    bodyContent = <pre className={styles.pre}>{content}</pre>
  }

  return (
    <div className={parsed.ok ? styles.viewer : styles.viewerError}>
      <div className={styles.header}>
        <div className={styles.title}>
          <span>{title || 'JSON预览'}</span>
          <Tag color={status.color}>{status.text}</Tag>
        </div>
        <Space size={8}>
          <Space.Compact size="small">
            <Button title="格式化视图" type={viewMode === 'formatted' ? 'primary' : 'default'} icon={<CodeOutlined />} onClick={() => setViewMode('formatted')} />
            <Button title="原始视图" type={viewMode === 'raw' ? 'primary' : 'default'} icon={<FileTextOutlined />} onClick={() => setViewMode('raw')} />
            <Button title="树形视图" type={viewMode === 'tree' ? 'primary' : 'default'} icon={<ApartmentOutlined />} onClick={() => setViewMode('tree')} />
          </Space.Compact>
          <Divider orientation="vertical" />
          <Button size="small" title="复制JSON" icon={<CopyOutlined />} onClick={() => void copy()} />
          <Button size="small" title="下载JSON" icon={<DownloadOutlined />} onClick={download} />
          {viewMode === 'tree' && (
            <Button
              size="small"
              title="展开/折叠所有"
              icon={allExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
              onClick={() => setAllExpanded(!allExpanded)}
            />
          )}
        </Space>
      </div>

      <div className={styles.content}>
        {bodyContent}
      </div>

      {parsed.ok && !isEmpty && (
        <div className={styles.footer}>
          <span>大小: {formatSize(new Blob([content]).size)}</span>
          <Divider orientation="vertical" />
          <span>行数: {content.split('\n').length}</span>
          <Divider orientation="vertical" />
          <span>节点: {countNodes(parsed.value)}</span>
        </div>
      )}
    </div>
  )
}
